import { api, requireAccount, withAccount, devRevealAll } from "./core";

export interface ModuleSignal {
  id: number;
  // Canonical module identifier. `module_path` from the new
  // observations_module_signals schema; `module` legacy alias kept
  // so any remaining legacy consumer still resolves.
  module: string;
  module_path?: string;
  // 4 structural scores, 0..100 ints. These are the new surface —
  // every Modules page widget should derive visuals from these
  // directly instead of reaching for legacy people-dimensioned
  // fields. `null` means the worker didn't emit the score (rare).
  survival_score?: number | null;
  change_pressure_score?: number | null;
  fragility_score?: number | null;
  ownership_concentration?: number | null;
  // Legacy people-dimensioned fields. Kept optional so existing
  // code paths compile, but the redesigned Modules page no longer
  // relies on them — the "modules are structural, people go on
  // Constellation" split means `top_author` etc. don't belong here.
  total_lines?: number;
  author_count?: number;
  top_author?: string;
  top_share?: number;
  entropy?: number;
  ownership_level?: string;
  survival_rate?: number;
  commit_count?: number;
  domain?: string;
  repo_name?: string;
  // Observation lineage — present on the /modules/recent read so the FE can
  // group rows by period and diff fragility / ownership_concentration
  // period-over-period.
  git_sha?: string;
  sha_author_date?: string;
}

export interface CochangePair {
  module_a: string;
  module_b: string;
  cochange_count: number;
  coupling: number;
  domain: string;
  repo_name?: string;
}

export interface ModuleLatestResponse {
  modules: ModuleSignal[];
  cochange: CochangePair[];
  // Module counts per domain (BE / FE / INFRA …). total_by_domain is the
  // galaxy-wide total before team scoping; team_by_domain is how many fall in
  // the current view — equal to total when unscoped, the team's holdings when a
  // ?team= scope is active. Drives the "X / Y per domain" coverage chips.
  total_by_domain?: Record<string, number>;
  team_by_domain?: Record<string, number>;
}

// One member's surviving gravity in a module — the spatial decomposition of
// their Survival, used to compute a structure-neutral Breadth (effective module
// count) on StarDetail.
export interface MemberModuleGravity {
  module_path: string;
  repo_name: string;
  survival_gravity: number;
}

// Module Ownership — the spatial decomposition of a module's surviving
// gravity across the people who hold it. This is a structural property
// of the *code* (how concentrated its stewardship is = bus-factor risk),
// not a ranking of who matters most. `concentration` SOLE_OWNER means a
// single steward holds the module: if they leave, the module risks
// collapse. Masked owners arrive pre-masked from the BE (github_user_id
// 0 + a `label`) per D-05 layered disclosure — never un-mask on the FE.
export interface ModuleOwner {
  github_user_id: number; // 0 when masked
  label?: string; // "Member-A" etc. present only when masked
  login?: string; // real github_login (module topology is always full-disclosure)
  survival_gravity: number;
  share: number; // 0..1 within the module
  is_viewer: boolean;
  // departed: this owner has left the galaxy (departed membership, or absent from
  // the live GitHub org). Their surviving gravity is "orphaned" — load-bearing
  // code with no resident steward. Always false for a masked owner.
  departed: boolean;
}
export interface ModuleOwnership {
  repo_name: string; // source repo; "" for repo-unknown (timeline) rows
  module_path: string;
  total_gravity: number;
  owner_count: number;
  top_share: number; // 0..1, share of the top owner
  concentration: "SOLE_OWNER" | "CONCENTRATED" | "HEALTHY" | "FRAGMENTED";
  owners: ModuleOwner[];
  // departed_share: fraction of this module's surviving gravity held by departed
  // owners (0..1). Collapse = concentration × departure. collapse_risk buckets it;
  // absent (undefined) when the module is healthy (below the at-risk floor).
  departed_share: number;
  collapse_risk?: "ORPHANED" | "AT_RISK";
  // domain: the source repo's free-form domain label (BE/FE/INFRA…), joined from
  // the repositories table by repo_name. Orthogonal filter axis for the Modules
  // BE/FE tabs and the Collapse-Risk view. "" / undefined when the repo can't be
  // matched (repo-unknown rows, removed/unmapped repos).
  domain?: string;
}

// Module Durability — the compact, consumable "is this module safe to
// change" profile: survival + ownership concentration fused into one coarse
// label (fragile/moderate/robust), sorted most-fragile-first by the backend.
// The same read backs the MCP server's module_durability tool (an AI agent's
// pre-flight check) — one shape, two consumers. Carries NO per-person
// identity (disclosure-light, unlike ModuleOwnership): there is no owners
// list to mask. collapse_risk / departed_share are the Nova-gated
// org-interpretation fields — the backend drops them (Go `omitempty`) for
// Free/Pro rows, so they arrive as `undefined`, never `null`/`0`, when gated.
export interface ModuleDurability {
  repo_name: string;
  module_path: string;
  domain: string;
  survival_score: number; // 0..100, 0 = no survival signal yet
  concentration: "SOLE_OWNER" | "CONCENTRATED" | "HEALTHY" | "FRAGMENTED";
  owner_count: number;
  top_share: number; // 0..1, share of the top owner
  collapse_risk?: "ORPHANED" | "AT_RISK";
  departed_share?: number; // 0..1, present only alongside collapse_risk (Nova)
  fragility: "fragile" | "moderate" | "robust";
  // maintainer_risk is the FORWARD abandonment early warning: a still-maintained
  // module with few surviving contributors, at risk of maintenance stopping and
  // the code being left to decay. Always present ("" when no exposure); an
  // always-visible structural signal like fragility (never gated). Distinct from
  // collapse_risk, which fires only after an owner has already left.
  maintainer_risk: "" | "elevated" | "critical";
}
export interface ModuleDurabilityResponse {
  modules: ModuleDurability[];
}

export const moduleApi = {
  list: (snapshotId: number) => api.get<ModuleSignal[]>(`/modules?snapshot_id=${snapshotId}`),
  // `team` narrows the module list to the modules that department/team works on
  // (modules where a team member has surviving gravity); omitted = whole galaxy.
  latest: (accountLogin: string, team?: number) =>
    api.get<ModuleLatestResponse>(
      withAccount(
        "/modules/latest",
        requireAccount(accountLogin, "moduleApi.latest"),
        team ? { team: String(team) } : undefined,
      ),
    ),
  // Per-module surviving gravity for one member (StarDetail Breadth). `month`
  // ("YYYY-MM") scopes the breakdown to the period the StarDetail scrubber is on
  // — the same snapshot that backs that month's 7-axis signal — so the breakdown
  // tracks the radar instead of always showing the latest cumulative snapshot.
  // Omitted = latest (no period selected / still loading).
  memberGravity: (accountLogin: string, githubUserId: number, month?: string) => {
    const p = new URLSearchParams();
    p.set("account_login", requireAccount(accountLogin, "moduleApi.memberGravity"));
    p.set("github_user_id", String(githubUserId));
    if (month) p.set("month", month);
    return api.get<MemberModuleGravity[]>(`/modules/member-gravity?${p}`);
  },
  // Flat module signals across the latest N observation periods, ASC. The FE
  // groups by git_sha and diffs period-over-period (Daily Observation).
  recent: (accountLogin: string, periods = 2) => {
    const p = new URLSearchParams();
    p.set("account_login", requireAccount(accountLogin, "moduleApi.recent"));
    p.set("periods", String(periods));
    return api.get<ModuleSignal[]>(`/modules/recent?${p}`);
  },
  cochange: (snapshotId: number) => api.get<CochangePair[]>(`/modules/cochange?snapshot_id=${snapshotId}`),
  // Per-module ownership decomposition — who sustains each module's
  // surviving gravity, and how concentrated that stewardship is.
  // dev_reveal_all mirrors galaxyApi: with the DEV toggle on, the ownership read
  // carries it so the backend bypasses the disclosure clamp (stg/local only, the
  // server still gates on DevToolsEnabled) — otherwise module topology stays
  // masked even under reveal-all because the flag never reaches the endpoint.
  ownership: (accountLogin: string) =>
    api.get<ModuleOwnership[]>(
      withAccount(
        "/modules/ownership",
        requireAccount(accountLogin, "moduleApi.ownership"),
        devRevealAll() ? { dev_reveal_all: "true" } : undefined,
      ),
    ),
  // Per-module fragile/robust profile — survival + ownership concentration
  // fused into one label, sorted most-fragile-first by the backend. Same
  // dev_reveal_all mirror as ownership() (the underlying read composes
  // GetModuleOwnership, which the reveal-all bypass targets).
  durability: (accountLogin: string) =>
    api.get<ModuleDurabilityResponse>(
      withAccount(
        "/modules/durability",
        requireAccount(accountLogin, "moduleApi.durability"),
        devRevealAll() ? { dev_reveal_all: "true" } : undefined,
      ),
    ),
  // Structural-debt meter: current ownerless-mass balance + its trend across
  // recent observations (the Structural Debt card). Non-personal, every plan.
  sludge: (accountLogin: string, periods = 12) =>
    api.get<SludgeMeterResponse>(
      withAccount("/modules/sludge", requireAccount(accountLogin, "moduleApi.sludge"), {
        periods: String(periods),
      }),
    ),
};

export interface SludgeWorstModule {
  repo_name: string;
  module_path: string;
  survival_score: number;
  concentration: string;
  reason: string;
}
export interface SludgePeriod {
  observed_at: string; // RFC3339
  sludge_mass: number;
  surviving_mass: number;
  sludge_ratio: number;
  sludge_module_count: number;
}
export interface SludgeMeterResponse {
  sludge_ratio: number;
  sludge_mass: number;
  surviving_mass: number;
  sludge_module_count: number;
  worst_modules: SludgeWorstModule[];
  trend: SludgePeriod[];
}
